import { HD_BODYGRAPH_CHANNELS, HD_BODYGRAPH_GATES_BY_CENTER } from '../hd-bodygraph-metadata';
import { HD_BODYGRAPH_GATE_TO_CENTER } from '../hd-bodygraph-metadata';
import { distanceToSegment } from '../annotation/hd-annotation-geometry';
import {
  HD_GATE_BLOCK_GAP,
  HD_GATE_BLOCK_SIZE,
  HD_GATE_BLOCK_SNAP_TOLERANCE,
  HD_GATE_BLOCK_VIEWBOX_HEIGHT,
  HD_GATE_BLOCK_VIEWBOX_WIDTH,
  HdGateBlockChannelSegment,
  HdGateBlockLayoutInput,
  HdGateBlockPlacement,
  HdGateBlockRect,
  hdGateBlockKey,
} from './hd-gate-block.models';

/**
 * Deterministische greedy plaatsing van planeetblok-clusters rond hun
 * poort-anker (spec §6). Pure functies, geen randomness: zelfde input →
 * identieke output. `distanceToSegment` wordt hergebruikt uit de
 * annotatie-geometrie.
 */

// ── Score-gewichten & marges (startwaarden; tunen = iteratie 3, spec §11) ──
const GATE_NUMBER_MARGIN = 4;
const BLOCK_MARGIN = 2;
const CHANNEL_HALF_WIDTH = 14;
const RING_BASE_OFFSET = 6;
const W_AMBIGUITY = 3000;
const W_DIRECTION = 40;
/** Binnenwaarts (center-interieur) is pas een optie als buiten niets vrij is. */
const W_INWARD = 260;
/** Expliciete per-poort voorkeursrichting weegt zwaarder dan de afgeleide. */
const W_GATE_PREFERENCE = 130;
/** Score-kost per eenheid zijwaartse verschuiving — gecentreerd blijft de default. */
const W_SLIDE = 0.4;
/** Expliciete voorkeur beloont alleen de éxacte richting (geen buur-smearing). */
const PREFERENCE_DOT_THRESHOLD = 0.92;
const FALLBACK_STEP = 8;
const FALLBACK_MAX_STEPS = 60;

/**
 * Kanaalbedekking is zone-gebaseerd (finetune 2026-07-05): blokjes óp het
 * eigen kanaalstuk is op de meeste plekken juist gewenst. Alleen de korte,
 * opeengepakte corridors zijn verboden terrein, en de drie middelste
 * verticale corridors vragen voorzichtigheid. Alle overige kanalen: gratis.
 */
const HD_GATE_BLOCK_CHANNEL_NO_COVER: ReadonlySet<string> = new Set([
  '64-47', '61-24', '63-4',   // hoofd ↔ ajna
  '17-62', '43-23', '11-56',  // ajna ↔ keel
]);
const HD_GATE_BLOCK_CHANNEL_CAREFUL: ReadonlySet<string> = new Set([
  '31-7', '8-1', '33-13',     // keel ↔ G
  '15-5', '2-14', '46-29',    // G ↔ sacraal
  '42-53', '3-60', '9-52',    // sacraal ↔ root
]);
const W_CHANNEL_NO_COVER = 400;
const W_CHANNEL_CAREFUL = 60;

function channelCoverageWeight(key: string): number {
  if (HD_GATE_BLOCK_CHANNEL_NO_COVER.has(key)) {
    return W_CHANNEL_NO_COVER;
  }
  if (HD_GATE_BLOCK_CHANNEL_CAREFUL.has(key)) {
    return W_CHANNEL_CAREFUL;
  }
  return 0;
}

/**
 * Magnetisch snappen tijdens het slepen (spec §8): rand-aan-rand met de
 * clustertussenruimte en as-uitlijning t.o.v. buurblokjes, per as onafhankelijk,
 * alleen binnen de tolerantie. Een buur doet enkel mee op de as waar de
 * dwars-as-banden overlappen ("ernaast" snapt horizontaal, "erboven/eronder"
 * verticaal), zodat verre blokken nooit magnetisch worden.
 */
export function snapHdGateBlockRect(
  rect: HdGateBlockRect,
  neighbors: readonly HdGateBlockRect[],
  tolerance: number = HD_GATE_BLOCK_SNAP_TOLERANCE,
): HdGateBlockRect {
  let bestDx: number | null = null;
  let bestDy: number | null = null;
  const consider = (delta: number, current: number | null): number | null =>
    Math.abs(delta) <= tolerance && (current == null || Math.abs(delta) < Math.abs(current))
      ? delta
      : current;

  for (const nb of neighbors) {
    const verticalOverlap = rect.y < nb.y + nb.height && nb.y < rect.y + rect.height;
    const horizontalOverlap = rect.x < nb.x + nb.width && nb.x < rect.x + rect.width;
    if (verticalOverlap) {
      bestDx = consider(nb.x + nb.width + HD_GATE_BLOCK_GAP - rect.x, bestDx);
      bestDx = consider(nb.x - rect.width - HD_GATE_BLOCK_GAP - rect.x, bestDx);
      bestDy = consider(nb.y - rect.y, bestDy); // rij-uitlijning met de zijbuur
    }
    if (horizontalOverlap) {
      bestDy = consider(nb.y + nb.height + HD_GATE_BLOCK_GAP - rect.y, bestDy);
      bestDy = consider(nb.y - rect.height - HD_GATE_BLOCK_GAP - rect.y, bestDy);
      bestDx = consider(nb.x - rect.x, bestDx); // kolom-uitlijning met de boven-/onderbuur
    }
  }
  if (bestDx == null && bestDy == null) {
    return rect;
  }
  return { ...rect, x: rect.x + (bestDx ?? 0), y: rect.y + (bestDy ?? 0) };
}

/** Houdt een blok-rect volledig binnen de bodygraph-viewBox. */
export function clampHdGateBlockRectToViewBox(rect: HdGateBlockRect): HdGateBlockRect {
  const x = Math.min(Math.max(rect.x, 0), HD_GATE_BLOCK_VIEWBOX_WIDTH - rect.width);
  const y = Math.min(Math.max(rect.y, 0), HD_GATE_BLOCK_VIEWBOX_HEIGHT - rect.height);
  return x === rect.x && y === rect.y ? rect : { ...rect, x, y };
}

/**
 * Zijwaartse offsets (haaks op de richting) per kandidaat; vaste volgorde.
 * Tot ±1,5 blokmaat: tussen twee dicht opeengepakte discs (bv. 9 en 52) past
 * geen blok, dus een rij moet ver genoeg opzij kunnen uitwijken.
 */
const SLIDE_OFFSETS: readonly number[] = [
  0,
  -HD_GATE_BLOCK_SIZE / 2, HD_GATE_BLOCK_SIZE / 2,
  -HD_GATE_BLOCK_SIZE, HD_GATE_BLOCK_SIZE,
  -HD_GATE_BLOCK_SIZE * 1.5, HD_GATE_BLOCK_SIZE * 1.5,
];

/** Score-bonus wanneer de clustervorm overeenkomt met de per-poort voorkeur. */
const W_SHAPE_PREFERENCE = 80;

interface HdGateBlockVector {
  readonly x: number;
  readonly y: number;
}

/** 8 windrichtingen, vaste volgorde (N eerst, met de klok mee) — spec §6. */
const RING_DIRECTIONS: readonly HdGateBlockVector[] = [
  { x: 0, y: -1 },
  { x: Math.SQRT1_2, y: -Math.SQRT1_2 },
  { x: 1, y: 0 },
  { x: Math.SQRT1_2, y: Math.SQRT1_2 },
  { x: 0, y: 1 },
  { x: -Math.SQRT1_2, y: Math.SQRT1_2 },
  { x: -1, y: 0 },
  { x: -Math.SQRT1_2, y: -Math.SQRT1_2 },
];

export type HdGateBlockDiagonalKind = 'diagonal-nw' | 'diagonal-ne' | 'diagonal-se' | 'diagonal-sw';
export type HdGateBlockShapeKind =
  | 'row' | 'column' | 'double-row' | 'double-column'
  | HdGateBlockDiagonalKind
  | 'diagonal-nw-double';

function isDiagonalKind(kind: HdGateBlockShapeKind): kind is HdGateBlockDiagonalKind {
  return kind === 'diagonal-nw' || kind === 'diagonal-ne' || kind === 'diagonal-se' || kind === 'diagonal-sw';
}

interface HdGateBlockGatePreference {
  readonly direction: HdGateBlockVector;
  /** Gewenste clustervorm, bv. 'row' voor "blokjes naast elkaar". */
  readonly shape?: HdGateBlockShapeKind;
  /**
   * Vaste slot-constellatie: blok-centrum-offsets t.o.v. het anker-centrum
   * (viewBox-eenheden), voor poorten waar geen enkele aaneengesloten
   * clustervorm past (bv. 43: één blok boven in het center, één aan elke
   * kant van de kanaalstub). Slots zijn geauthoreerde waarheid en worden
   * zonder obstakel-toetsing toegepast zodra het aantal blokjes ≤ het aantal
   * slots (en alles binnen de viewBox valt); anders normale zoektocht,
   * waarvoor `direction`/`shape` als hint blijven gelden.
   */
  readonly slots?: readonly HdGateBlockVector[];
  /**
   * Geauthoreerde eindverschuiving (viewBox-eenheden) bovenop het
   * zoekresultaat — voor "een paar pixels die kant op"-tuning zonder de hele
   * poort op slots te zetten. Wordt ná de kandidaat-keuze toegepast, zonder
   * hertoetsing (zelfde filosofie als slots). Niet van toepassing op slots.
   */
  readonly nudge?: HdGateBlockVector;
}

/**
 * Handmatige voorkeur per poort — override op de afgeleide van-het-center-af
 * richting, voor plekken waar die afleiding visueel de verkeerde kant kiest
 * (finetune 2026-07-05 o.b.v. echte charts + Figma-referentie). Richting weegt
 * via {@link W_GATE_PREFERENCE}, vorm via {@link W_SHAPE_PREFERENCE}.
 */
const NW: HdGateBlockVector = { x: -Math.SQRT1_2, y: -Math.SQRT1_2 };
const NE: HdGateBlockVector = { x: Math.SQRT1_2, y: -Math.SQRT1_2 };
const SE: HdGateBlockVector = { x: Math.SQRT1_2, y: Math.SQRT1_2 };
const SW: HdGateBlockVector = { x: -Math.SQRT1_2, y: Math.SQRT1_2 };
const N: HdGateBlockVector = { x: 0, y: -1 };
const S: HdGateBlockVector = { x: 0, y: 1 };

/**
 * 43-stijl slot-constellatie voor poorten waarvan de kanaalstub omlaag
 * vertrekt: flankers wéérszijden van de stub eronder, derde blok er vlak
 * boven ín het eigen center. Voorkeursvolgorde links → rechts → center;
 * de slot-kiezer slaat bezette slots over (geactiveerde buurpoort of al
 * geplaatst blok), dus een enkel blokje kiest vanzelf de vrije flank.
 */
const SLOTS_FLANKS_BELOW: readonly HdGateBlockVector[] = [
  { x: -38, y: 41 },
  { x: 38, y: 41 },
  { x: 0, y: -51 },
];

/**
 * 23-stijl spiegel voor poorten waarvan de kanaalstub omhoog vertrekt:
 * flankers erboven, derde blok er vlak onder ín het eigen center.
 * Voorkeursvolgorde rechts → links → center — de primaire flank is bewust
 * gespiegeld t.o.v. {@link SLOTS_FLANKS_BELOW}, zodat de twee uiteinden van
 * een korte corridor (bv. 3↔60) elkaars flank niet raken zolang elk ≤ 2
 * blokjes heeft.
 */
const SLOTS_FLANKS_ABOVE: readonly HdGateBlockVector[] = [
  { x: 38, y: -41 },
  { x: -38, y: -41 },
  { x: 0, y: 51 },
];

/**
 * Trap voor de milt-poorten van de root-waaier (32/28/18), langs hun
 * kanalen (32-54, 28-38, 18-58). Helling ~35° — de generieke 45°-diagonaal
 * dook in het onderliggende kanaal, de eerste vlakke versie (~26°) hing
 * over de rechterbuur (bv. 28-blokjes over de 32-disc). Integraal iets
 * lager gelegd: bij twijfel liever richting de vrije ruimte onderaan
 * (18-kant) dan over de buurpoort (finetune 2026-07-05, 2 rondes).
 */
const SLOTS_MILT_STAIR_SE: readonly HdGateBlockVector[] = [
  { x: 46, y: 26 },
  { x: 100, y: 64 },
  { x: 154, y: 102 },
];

/** Spiegel voor de solarplexus-kant (49/55/30): trap omlaag-links. */
const SLOTS_SOLAR_STAIR_SW: readonly HdGateBlockVector[] = [
  { x: -46, y: 26 },
  { x: -100, y: 64 },
  { x: -154, y: 102 },
];

/**
 * Root-kant van de linker waaier, omhoog-links op de eigen stub (zelfde
 * vlakke helling als de milt-trap, gespiegeld). Voor 54: zonder slots vluchtte
 * een enkele activatie naar de 38-zone zodra de corridor druk was, terwijl er
 * boven 54 gewoon plaats is (finetune 2026-07-05).
 */
const SLOTS_ROOT_STAIR_NW: readonly HdGateBlockVector[] = [
  { x: -46, y: -26 },
  { x: -100, y: -64 },
  { x: -154, y: -102 },
];

/**
 * Een slot telt pas als bezet bij substantiële overlap: krap naast een
 * buurtrap staan mag ("het is krap maar kan wel" — finetune 2026-07-05),
 * dus randcontact tot deze marge diskwalificeert een slot niet.
 */
const SLOT_OVERLAP_TOLERANCE = 8;

const HD_GATE_BLOCK_DIRECTION_PREFERENCES: Readonly<Partial<Record<number, HdGateBlockGatePreference>>> = {
  9: { direction: S, shape: 'row' },                       // rij eronder, slide schuift 'm van 52/3 weg
  11: { direction: { x: 1, y: 0 }, shape: 'double-row' },  // strak rechts van 11: ☉ boven, ♆♆ eronder
  12: { direction: SE },                                   // rechtsonder, de steil dalende kanalen volgend (weg van 35)
  // 34: dubbele trap omhoog-links langs de integratie-bundel — treden van
  // twee gestapelde blokjes, zodat een drukke 34 niet uit de hand loopt.
  // Nudge: het geheel omlaag zodat de starttrede duidelijk náást 34 staat
  // (finetune in twee rondes: 10, daarna nog een half blok erbij).
  34: { direction: NW, shape: 'diagonal-nw-double', nudge: { x: 0, y: 38 } },
  // 44: rij rechtsboven óp de licht stijgende 26-44 stub (kanaal bedekken is
  // daar gratis) — laat de ruimte links vrij voor de 57-blokjes. De nudge
  // trekt de rij nét onder 57's zone (finetune in twee rondes bijgeschoven).
  44: { direction: NE, shape: 'row', nudge: { x: 8, y: 23 } },
  // 61: kolom recht boven de poort — een rij spreidt over de zones van 64/63
  // en is daar verwarrend (midden-hoofdpoort heeft geen afgeleide richting).
  61: { direction: N, shape: 'column' },
  // 46: kolom rechts naast de 46-29 stub — links bedekte poort 2, en zo
  // blijft daar plaats voor een eventuele 2-activatie.
  46: { direction: SE, shape: 'column', slots: [{ x: 30, y: 49 }, { x: 30, y: 103 }] },
  50: { direction: { x: 1, y: 0 }, shape: 'row' },         // rij rechts op de 27-50 stub → 44/57 krijgen hun eigen plek

  // De verticale corridor-poorten: 43/23-stijl flank-constellaties (zie de
  // SLOTS_FLANKS_*-constanten). Stub omlaag → flankers onder; stub omhoog →
  // flankers boven, met gespiegelde vulvolgorde tegen flank-botsingen.
  43: { direction: SW, shape: 'row', slots: SLOTS_FLANKS_BELOW },  // ajna-tip (krapste poort)
  23: { direction: N, shape: 'row', slots: SLOTS_FLANKS_ABOVE },   // keel-top, spiegel van 43
  8: { direction: S, shape: 'row', slots: SLOTS_FLANKS_BELOW },    // keel-onderkant (8-1)
  1: { direction: N, shape: 'row', slots: SLOTS_FLANKS_ABOVE },    // G-top (8-1)
  2: { direction: S, shape: 'row', slots: SLOTS_FLANKS_BELOW },    // G-onderkant (2-14)
  14: { direction: N, shape: 'row', slots: SLOTS_FLANKS_ABOVE },   // sacraal-top (2-14)
  3: { direction: S, shape: 'row', slots: SLOTS_FLANKS_BELOW },    // sacraal-onderkant (3-60)
  60: { direction: N, shape: 'row', slots: SLOTS_FLANKS_ABOVE },   // root-top (3-60)

  // De diagonale waaier rond de root: trappen parallel aan de kanalen.
  // Root-kant links (32-54, 28-38, 18-58) → trap omhoog-links. 54 heeft
  // vaste slots op zijn stub (zie SLOTS_ROOT_STAIR_NW); 38/58 werken goed
  // via de zoektocht en blijven daar bewust op.
  54: { direction: NW, shape: 'diagonal-nw', slots: SLOTS_ROOT_STAIR_NW },
  38: { direction: NW, shape: 'diagonal-nw' },
  58: { direction: NW, shape: 'diagonal-nw' },
  // Milt-kant van dezelfde waaier → gespiegeld, vlakke trap omlaag-rechts
  // via slots (de generieke 45°-diagonaal was te steil voor deze kanalen):
  32: { direction: SE, shape: 'diagonal-se', slots: SLOTS_MILT_STAIR_SE },
  28: { direction: SE, shape: 'diagonal-se', slots: SLOTS_MILT_STAIR_SE },
  18: { direction: SE, shape: 'diagonal-se', slots: SLOTS_MILT_STAIR_SE },
  // Root-kant rechts (19-49, 39-55, 30-41) → trap omhoog-rechts:
  19: { direction: NE, shape: 'diagonal-ne' },
  39: { direction: NE, shape: 'diagonal-ne' },
  41: { direction: NE, shape: 'diagonal-ne' },
  // Solarplexus-kant van dezelfde waaier → gespiegeld, vlakke trap
  // omlaag-links via slots (zelfde vlakke helling als de milt-kant):
  49: { direction: SW, shape: 'diagonal-sw', slots: SLOTS_SOLAR_STAIR_SW },
  55: { direction: SW, shape: 'diagonal-sw', slots: SLOTS_SOLAR_STAIR_SW },
  30: { direction: SW, shape: 'diagonal-sw', slots: SLOTS_SOLAR_STAIR_SW },
};

export interface HdGateBlockShapeLayout {
  /** Top-left offsets per blok, relatief aan de cluster-bounding-box. */
  readonly offsets: readonly HdGateBlockVector[];
  readonly width: number;
  readonly height: number;
  readonly kind: HdGateBlockShapeKind;
}

interface ShapeGrid {
  cols: number;
  rows: number;
  columnMajor: boolean;
  kind: HdGateBlockShapeKind;
}

function buildShapeOffsets(count: number, grid: ShapeGrid): HdGateBlockShapeLayout {
  const step = HD_GATE_BLOCK_SIZE + HD_GATE_BLOCK_GAP;
  const width = grid.cols * HD_GATE_BLOCK_SIZE + (grid.cols - 1) * HD_GATE_BLOCK_GAP;
  const height = grid.rows * HD_GATE_BLOCK_SIZE + (grid.rows - 1) * HD_GATE_BLOCK_GAP;
  const offsets: HdGateBlockVector[] = [];
  for (let i = 0; i < count; i++) {
    const col = grid.columnMajor ? Math.floor(i / grid.rows) : i % grid.cols;
    const row = grid.columnMajor ? i % grid.rows : Math.floor(i / grid.cols);
    let x = col * step;
    let y = row * step;
    if (!grid.columnMajor) {
      // Partiële laatste rij centreren.
      const lastRow = Math.floor((count - 1) / grid.cols);
      const lastCount = count - lastRow * grid.cols;
      if (row === lastRow && lastCount < grid.cols) {
        x += ((grid.cols - lastCount) * step) / 2;
      }
    } else {
      // Partiële laatste kolom centreren.
      const lastCol = Math.floor((count - 1) / grid.rows);
      const lastCount = count - lastCol * grid.rows;
      if (col === lastCol && lastCount < grid.rows) {
        y += ((grid.rows - lastCount) * step) / 2;
      }
    }
    offsets.push({ x, y });
  }
  return { offsets, width, height, kind: grid.kind };
}

/** Vormvarianten per clustergrootte: rij, kolom, en vanaf 3 dubbelrij/-kolom (spec §6). */
export function buildHdGateBlockShapeLayouts(count: number): readonly HdGateBlockShapeLayout[] {
  const grids: ShapeGrid[] = [{ cols: count, rows: 1, columnMajor: false, kind: 'row' }];
  if (count > 1) {
    grids.push({ cols: 1, rows: count, columnMajor: true, kind: 'column' });
  }
  if (count >= 3) {
    grids.push({ cols: Math.ceil(count / 2), rows: 2, columnMajor: false, kind: 'double-row' });
    grids.push({ cols: 2, rows: Math.ceil(count / 2), columnMajor: true, kind: 'double-column' });
  }
  return grids.map(grid => buildShapeOffsets(count, grid));
}

/**
 * Trapvorm langs de diagonale waaier-kanalen rond de root (32-54, 28-38,
 * 18-58 links; 19-49, 39-55, 30-41 rechts). Vier spiegelvarianten; de
 * benaming is de richting waarin de trap wegloopt van het eerste blok, en
 * `blocks[0]` ligt altijd op de trede die het dichtst bij de poort komt bij
 * plaatsing in diezelfde richting. Wordt alleen gegenereerd voor poorten
 * waarvan de voorkeur er expliciet om vraagt.
 */
export function buildHdGateBlockDiagonalShapeLayout(
  count: number,
  kind: HdGateBlockDiagonalKind,
): HdGateBlockShapeLayout {
  const step = HD_GATE_BLOCK_SIZE + HD_GATE_BLOCK_GAP;
  const offsets: HdGateBlockVector[] = [];
  for (let i = 0; i < count; i++) {
    const near = i;              // trede-index vanaf het blok dichtst bij de poort
    const far = count - 1 - i;   // trede-index vanaf het verste blok
    switch (kind) {
      case 'diagonal-nw': offsets.push({ x: far * step, y: far * step }); break;   // blocks[0] rechtsonder
      case 'diagonal-ne': offsets.push({ x: near * step, y: far * step }); break;  // blocks[0] linksonder
      case 'diagonal-se': offsets.push({ x: near * step, y: near * step }); break; // blocks[0] linksboven
      case 'diagonal-sw': offsets.push({ x: far * step, y: near * step }); break;  // blocks[0] rechtsboven
    }
  }
  const extent = count * HD_GATE_BLOCK_SIZE + (count - 1) * HD_GATE_BLOCK_GAP;
  return { offsets, width: extent, height: extent, kind };
}

/**
 * Verticale stijging per trede van de dubbele trap: een half blok — een
 * volledige blokmaat maakte de trap te steil (finetune 2026-07-05).
 */
const DOUBLE_STAIR_RISE = HD_GATE_BLOCK_SIZE / 2 + HD_GATE_BLOCK_GAP;

/**
 * Dubbele trap omhoog-links: elke trede is een paartje van twee gestapelde
 * blokjes (blocks[0] boven, blocks[1] eronder = onderste/dichtstbijzijnde
 * trede), elk volgend paar één blokbreedte naar links en een half blok
 * omhoog. Halveert de traplengte voor drukke poorten zoals 34.
 */
export function buildHdGateBlockDoubleDiagonalNwLayout(count: number): HdGateBlockShapeLayout {
  const step = HD_GATE_BLOCK_SIZE + HD_GATE_BLOCK_GAP;
  const treads = Math.ceil(count / 2);
  const offsets: HdGateBlockVector[] = [];
  for (let i = 0; i < count; i++) {
    const tread = Math.floor(i / 2);          // trede 0 = rechtsonder, dichtst bij de poort
    const withinTread = i % 2;                // 0 = bovenste van het paar, 1 = onderste
    const fromLeft = treads - 1 - tread;
    offsets.push({ x: fromLeft * step, y: fromLeft * DOUBLE_STAIR_RISE + withinTread * step });
  }
  const width = treads * HD_GATE_BLOCK_SIZE + (treads - 1) * HD_GATE_BLOCK_GAP;
  const height = (treads - 1) * DOUBLE_STAIR_RISE + 2 * HD_GATE_BLOCK_SIZE + HD_GATE_BLOCK_GAP;
  return { offsets, width, height, kind: 'diagonal-nw-double' };
}

/** Recht segment tussen de anker-centra van beide poorten van elk kanaal (spec §5). */
export function buildHdGateBlockChannelSegments(
  gateAnchors: ReadonlyMap<number, HdGateBlockRect>,
): HdGateBlockChannelSegment[] {
  const segments: HdGateBlockChannelSegment[] = [];
  for (const channel of HD_BODYGRAPH_CHANNELS) {
    const a = gateAnchors.get(channel.gates[0]);
    const b = gateAnchors.get(channel.gates[1]);
    if (!a || !b) {
      continue;
    }
    const x1 = a.x + a.width / 2;
    const y1 = a.y + a.height / 2;
    const x2 = b.x + b.width / 2;
    const y2 = b.y + b.height / 2;
    const length = Math.hypot(x2 - x1, y2 - y1);
    if (length === 0) {
      continue;
    }
    segments.push({ key: channel.key, x1, y1, x2, y2, length });
  }
  return segments;
}

// ── Rect-helpers ──────────────────────────────────────────────────────────

function inflate(rect: HdGateBlockRect, margin: number): HdGateBlockRect {
  return { x: rect.x - margin, y: rect.y - margin, width: rect.width + 2 * margin, height: rect.height + 2 * margin };
}

function rectsIntersect(a: HdGateBlockRect, b: HdGateBlockRect): boolean {
  return a.x < b.x + b.width && b.x < a.x + a.width && a.y < b.y + b.height && b.y < a.y + a.height;
}

function rectCenter(rect: HdGateBlockRect): HdGateBlockVector {
  return { x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 };
}

function insideViewBox(rect: HdGateBlockRect): boolean {
  return rect.x >= 0 && rect.y >= 0
    && rect.x + rect.width <= HD_GATE_BLOCK_VIEWBOX_WIDTH
    && rect.y + rect.height <= HD_GATE_BLOCK_VIEWBOX_HEIGHT;
}

/**
 * Voorkeursrichting: van de centroïde van de center-poortankers weg (spec §6).
 * Geen shape-parsing nodig; de centroïde volgt uit de ankers van de poorten
 * die in hetzelfde center liggen. `null` bij ontbrekende data.
 */
function outwardDirection(gate: number, gateAnchors: ReadonlyMap<number, HdGateBlockRect>): HdGateBlockVector | null {
  const centerKey = HD_BODYGRAPH_GATE_TO_CENTER[gate];
  const anchor = gateAnchors.get(gate);
  if (!centerKey || !anchor) {
    return null;
  }
  const siblingGates = HD_BODYGRAPH_GATES_BY_CENTER[centerKey] ?? [];
  let sumX = 0;
  let sumY = 0;
  let n = 0;
  for (const sibling of siblingGates) {
    const rect = gateAnchors.get(sibling);
    if (!rect) {
      continue;
    }
    const c = rectCenter(rect);
    sumX += c.x;
    sumY += c.y;
    n++;
  }
  if (n < 2) {
    return null; // te weinig ankers om een richting uit af te leiden
  }
  const own = rectCenter(anchor);
  const dx = own.x - sumX / n;
  const dy = own.y - sumY / n;
  const length = Math.hypot(dx, dy);
  return length < 1e-6 ? null : { x: dx / length, y: dy / length };
}

// ── Kandidaten & score ────────────────────────────────────────────────────

interface Candidate {
  readonly rects: readonly HdGateBlockRect[];
  readonly clusterRect: HdGateBlockRect;
  readonly direction: HdGateBlockVector;
  /** Ring-0 (tegen het anker aan) — associatie met de eigen poort is dan evident. */
  readonly adjacent: boolean;
  /** Zijwaartse verschuiving (haaks op de richting), in viewBox-eenheden. */
  readonly slide: number;
  readonly shapeKind: HdGateBlockShapeKind;
}

function buildCandidate(
  anchor: HdGateBlockRect,
  shape: HdGateBlockShapeLayout,
  direction: HdGateBlockVector,
  extraOffset: number,
  slide: number,
): Candidate {
  const anchorC = rectCenter(anchor);
  // Support-afstanden van beide AABB's langs de richting: cluster raakt het
  // anker nét niet, plus vaste basisafstand. `slide` schuift de cluster haaks
  // op de richting — zo kan hij naast een buurpoort-nummer uitwijken zonder
  // van zijn eigen poort weg te drijven (finetune 2026-07-05).
  const anchorHalf = Math.abs(direction.x) * (anchor.width / 2) + Math.abs(direction.y) * (anchor.height / 2);
  const clusterHalf = Math.abs(direction.x) * (shape.width / 2) + Math.abs(direction.y) * (shape.height / 2);
  const offset = anchorHalf + clusterHalf + RING_BASE_OFFSET + extraOffset;
  const cx = anchorC.x + direction.x * offset - direction.y * slide;
  const cy = anchorC.y + direction.y * offset + direction.x * slide;
  const clusterRect: HdGateBlockRect = {
    x: cx - shape.width / 2,
    y: cy - shape.height / 2,
    width: shape.width,
    height: shape.height,
  };
  const rects = shape.offsets.map(o => ({
    x: clusterRect.x + o.x,
    y: clusterRect.y + o.y,
    width: HD_GATE_BLOCK_SIZE,
    height: HD_GATE_BLOCK_SIZE,
  }));
  return { rects, clusterRect, direction, adjacent: extraOffset === 0, slide, shapeKind: shape.kind };
}

function violatesHardRules(
  candidate: Candidate,
  numberObstacles: readonly HdGateBlockRect[],
  blockObstacles: readonly HdGateBlockRect[],
): boolean {
  for (const rect of candidate.rects) {
    if (!insideViewBox(rect)) {
      return true;
    }
    for (const obstacle of numberObstacles) {
      if (rectsIntersect(rect, obstacle)) {
        return true;
      }
    }
    for (const obstacle of blockObstacles) {
      if (rectsIntersect(rect, obstacle)) {
        return true;
      }
    }
  }
  return false;
}

function scoreCandidate(
  candidate: Candidate,
  gate: number,
  anchor: HdGateBlockRect,
  clusterGates: readonly number[],
  gateAnchors: ReadonlyMap<number, HdGateBlockRect>,
  channelSegments: readonly HdGateBlockChannelSegment[],
): number {
  const c = rectCenter(candidate.clusterRect);
  const anchorC = rectCenter(anchor);
  const ownDist = Math.hypot(c.x - anchorC.x, c.y - anchorC.y);
  let score = ownDist;

  // Ambiguïteit: dichter bij een ándere poort-met-blokjes dan bij de eigen
  // (spec §6). Alleen voor zwevende kandidaten — een blokje dat tegen zijn
  // eigen disc plakt kan niet bij de buurpoort horen; zonder deze uitzondering
  // evacueren clusters uit dichte zones zoals de milt-hoek (finetune 2026-07-05).
  if (!candidate.adjacent) {
    for (const otherGate of clusterGates) {
      if (otherGate === gate) {
        continue;
      }
      const other = gateAnchors.get(otherGate);
      if (!other) {
        continue;
      }
      const otherC = rectCenter(other);
      if (Math.hypot(c.x - otherC.x, c.y - otherC.y) < ownDist) {
        score += W_AMBIGUITY;
        break;
      }
    }
  }

  // Kanaalbedekking: zone-gebaseerd — op de meeste kanalen is bedekken juist
  // gewenst (gratis); alleen de corridors tellen (finetune 2026-07-05).
  for (const rect of candidate.rects) {
    const blockC = rectCenter(rect);
    for (const seg of channelSegments) {
      const weight = channelCoverageWeight(seg.key);
      if (weight === 0) {
        continue;
      }
      const dist = distanceToSegment(blockC, { x: seg.x1, y: seg.y1 }, { x: seg.x2, y: seg.y2 });
      if (dist < CHANNEL_HALF_WIDTH + HD_GATE_BLOCK_SIZE / 2) {
        score += weight;
      }
    }
  }

  // Zijwaartse verschuiving kost een beetje: gecentreerd blijft de default.
  score += W_SLIDE * Math.abs(candidate.slide);

  // Richting: expliciete per-poort voorkeur > afgeleide van-het-center-af.
  // De expliciete voorkeur beloont alleen de éxacte richting (scherp), anders
  // wint een vrije buurrichting het alsnog van de bedoelde plek. Tegen de
  // referentierichting in (center-interieur, of tegengesteld aan de voorkeur)
  // is pas een optie als daarbuiten niets vrij is (W_INWARD).
  const preference = HD_GATE_BLOCK_DIRECTION_PREFERENCES[gate];
  const reference = preference?.direction ?? outwardDirection(gate, gateAnchors);
  if (reference) {
    const dot = candidate.direction.x * reference.x + candidate.direction.y * reference.y;
    if (dot < 0) {
      score += W_INWARD * -dot;
    } else if (preference) {
      score -= dot >= PREFERENCE_DOT_THRESHOLD ? W_GATE_PREFERENCE : 0;
    } else {
      score -= W_DIRECTION * dot;
    }
  }

  // Vorm-voorkeur ("blokjes naast elkaar"): rij vs kolom is anders puur een
  // afstandskwestie en dan wint de kolom langs zijn plaatsingsrichting altijd.
  if (preference?.shape && candidate.shapeKind === preference.shape) {
    score -= W_SHAPE_PREFERENCE;
  }
  return score;
}

// ── Hoofdfunctie ──────────────────────────────────────────────────────────

export function computeHdGateBlockLayout(input: HdGateBlockLayoutInput): HdGateBlockPlacement[] {
  const { gateAnchors, channelSegments } = input;
  const placements: HdGateBlockPlacement[] = [];
  // Obstakel-administratie gesplitst (spec §8): geplaatste blokken én de
  // ghost-slots van gepinde clustergenoten (zie de emissie onderaan) staan in
  // `blockObstacles`; de pin-rects zélf staan apart per poort. Voor de
  // plaatsing van poort g gelden als obstakel: `blockObstacles` + de pins van
  // álle ándere poorten — de eigen pins van g tellen bewust níét mee. Anders
  // duwt een pin die dicht bij de oude clusterplek gedropt wordt de eigen
  // phantom-opstelling alsnog opzij (Stevens "2de blokje verspringt"-bug).
  const blockObstacles: HdGateBlockRect[] = [];
  const pinObstaclesByGate = new Map<number, HdGateBlockRect[]>();
  const numberObstacles = [...gateAnchors.values()].map(rect => inflate(rect, GATE_NUMBER_MARGIN));

  // 1. Pins eerst: exacte positie (anker + offset), als pinned placement +
  //    obstakel (spec §8). Het obstakel wordt per poort bijgehouden.
  const pinnedKeys = new Set<string>();
  for (const pin of input.pins) {
    const anchor = gateAnchors.get(pin.gate);
    const clusterForPin = input.clusters.find(c => c.gate === pin.gate);
    const descriptor = clusterForPin?.blocks.find(
      b => b.planetId === pin.planetId && b.side === pin.side,
    );
    if (!anchor || !descriptor) {
      continue; // pin zonder (nog) bestaande activatie → negeren, niet verwijderen (spec §9)
    }
    const rect: HdGateBlockRect = {
      x: anchor.x + pin.dx,
      y: anchor.y + pin.dy,
      width: HD_GATE_BLOCK_SIZE,
      height: HD_GATE_BLOCK_SIZE,
    };
    placements.push({ descriptor, rect, pinned: true });
    const existing = pinObstaclesByGate.get(pin.gate);
    if (existing) {
      existing.push(inflate(rect, BLOCK_MARGIN));
    } else {
      pinObstaclesByGate.set(pin.gate, [inflate(rect, BLOCK_MARGIN)]);
    }
    pinnedKeys.add(hdGateBlockKey(descriptor));
  }

  // Pins van álle ándere poorten dan `gate` — obstakel voor de plaatsing van
  // die poort; de eigen pins horen hier bewust níét in (zie boven).
  const foreignPins = (gate: number): HdGateBlockRect[] => {
    const result: HdGateBlockRect[] = [];
    for (const [otherGate, rects] of pinObstaclesByGate) {
      if (otherGate !== gate) {
        result.push(...rects);
      }
    }
    return result;
  };

  // 2. Clusters plaatsen met hun VOLLEDIGE bloklijst — het pinnen van één lid
  //    mag de opstelling van de rest niet veranderen, dus de phantom-opstelling
  //    (vorm, positie én plaatsingsvolgorde) is identiek aan die vóór het
  //    pinnen: gepinde leden tellen mee voor de groottes en de §3-volgorde.
  //    Volledig gepinde clusters (alle leden gepind) slaan de plaatsing over.
  const autoClusters = input.clusters
    .filter(c => gateAnchors.has(c.gate) && c.blocks.some(b => !pinnedKeys.has(hdGateBlockKey(b))))
    .map(c => ({ gate: c.gate, blocks: c.blocks }))
    .sort((a, b) => b.blocks.length - a.blocks.length || a.gate - b.gate);

  const clusterGates = input.clusters
    .filter(c => gateAnchors.has(c.gate))
    .map(c => c.gate);

  for (const clusterToPlace of autoClusters) {
    const anchor = gateAnchors.get(clusterToPlace.gate)!;
    const preference = HD_GATE_BLOCK_DIRECTION_PREFERENCES[clusterToPlace.gate];
    // Obstakelset voor deze poort: geplaatste blokken + ghosts + de pins van
    // álle ándere poorten (niet de eigen). Blijft stabiel tot de emissie.
    const obstacles = [...blockObstacles, ...foreignPins(clusterToPlace.gate)];

    // Emissie per lid: ongepinde leden krijgen hun rect uit de volledige
    // phantom-opstelling; gepinde leden zijn al bovenaan als pinned-placement
    // geëmitteerd en laten hier alleen hun opstellings-slot als "ghost"-
    // obstakel achter. Stabiliteit boven ruimtewinst: de gebruiker is handmatig
    // aan het schikken, dus buurclusters mogen niet in de vrijgekomen plek
    // trekken (die zouden anders óók verspringen bij de drop).
    const emit = (rects: readonly HdGateBlockRect[]): void => {
      for (let i = 0; i < clusterToPlace.blocks.length; i++) {
        const descriptor = clusterToPlace.blocks[i];
        const rect = rects[i];
        if (!pinnedKeys.has(hdGateBlockKey(descriptor))) {
          placements.push({ descriptor, rect, pinned: false });
        }
        blockObstacles.push(inflate(rect, BLOCK_MARGIN));
      }
    };

    // Vaste slot-constellatie: geauthoreerde waarheid — de posities zijn
    // visueel gevalideerd (Figma-referentie), dus geen generieke obstakel-
    // toetsing die alles stilletjes laat terugvallen. Wél activatie-bewust
    // kiezen: elk blok pakt het eerste vríje slot — bezet is een slot dat het
    // anker van een ándere geactiveerde poort of een al geplaatst blok/ghost/
    // vreemde pin overlapt (bv. 23 met één blokje kiest de 62-kant als 56
    // geactiveerd is). Alles bezet → gewoon de authored volgorde. Vangnetten:
    // het aantal blokjes moet passen en alles moet binnen de viewBox blijven.
    if (preference?.slots && clusterToPlace.blocks.length <= preference.slots.length) {
      const anchorC = rectCenter(anchor);
      const slots = preference.slots;
      const slotRectAt = (slotIndex: number): HdGateBlockRect => ({
        x: anchorC.x + slots[slotIndex].x - HD_GATE_BLOCK_SIZE / 2,
        y: anchorC.y + slots[slotIndex].y - HD_GATE_BLOCK_SIZE / 2,
        width: HD_GATE_BLOCK_SIZE,
        height: HD_GATE_BLOCK_SIZE,
      });
      const activatedAnchorObstacles = clusterGates
        .filter(otherGate => otherGate !== clusterToPlace.gate)
        .map(otherGate => gateAnchors.get(otherGate))
        .filter((rect): rect is HdGateBlockRect => rect != null)
        .map(rect => inflate(rect, GATE_NUMBER_MARGIN));
      const usedSlots = new Set<number>();
      const chosenRects: HdGateBlockRect[] = [];
      for (let b = 0; b < clusterToPlace.blocks.length; b++) {
        let pick = -1;
        for (let i = 0; i < slots.length; i++) {
          if (usedSlots.has(i)) {
            continue;
          }
          const probe = inflate(slotRectAt(i), -SLOT_OVERLAP_TOLERANCE);
          const blocked = activatedAnchorObstacles.some(o => rectsIntersect(probe, o))
            || obstacles.some(o => rectsIntersect(probe, o));
          if (!blocked) {
            pick = i;
            break;
          }
        }
        if (pick < 0) {
          for (let i = 0; i < slots.length; i++) {
            if (!usedSlots.has(i)) {
              pick = i;
              break;
            }
          }
        }
        usedSlots.add(pick);
        chosenRects.push(slotRectAt(pick));
      }
      if (chosenRects.every(insideViewBox)) {
        emit(chosenRects);
        continue;
      }
    }

    const shapes: HdGateBlockShapeLayout[] = [...buildHdGateBlockShapeLayouts(clusterToPlace.blocks.length)];
    const preferredShape = preference?.shape;
    if (preferredShape && isDiagonalKind(preferredShape) && clusterToPlace.blocks.length > 1) {
      shapes.push(buildHdGateBlockDiagonalShapeLayout(clusterToPlace.blocks.length, preferredShape));
    }
    if (preferredShape === 'diagonal-nw-double' && clusterToPlace.blocks.length > 1) {
      shapes.push(buildHdGateBlockDoubleDiagonalNwLayout(clusterToPlace.blocks.length));
    }

    let best: Candidate | null = null;
    let bestScore = Infinity;
    let firstCandidate: Candidate | null = null;

    for (const shape of shapes) {
      for (const direction of RING_DIRECTIONS) {
        for (const ringStep of [0, 1]) {
          for (const slide of SLIDE_OFFSETS) {
            const candidate = buildCandidate(anchor, shape, direction, ringStep * HD_GATE_BLOCK_SIZE, slide);
            firstCandidate ??= candidate;
            if (violatesHardRules(candidate, numberObstacles, obstacles)) {
              continue;
            }
            const score = scoreCandidate(
              candidate, clusterToPlace.gate, anchor, clusterGates, gateAnchors, channelSegments,
            );
            if (score < bestScore) {
              bestScore = score;
              best = candidate;
            }
          }
        }
      }
    }

    // Vangnet (spec §6): geen enkele kandidaat vrij → eerste kandidaat in vaste
    // stappen langs zijn richting schuiven tot hij vrij is (begrensd).
    if (!best && firstCandidate) {
      let fallback = firstCandidate;
      for (let step = 1; step <= FALLBACK_MAX_STEPS; step++) {
        const dx = firstCandidate.direction.x * FALLBACK_STEP * step;
        const dy = firstCandidate.direction.y * FALLBACK_STEP * step;
        const moved: Candidate = {
          direction: firstCandidate.direction,
          adjacent: false,
          slide: firstCandidate.slide,
          shapeKind: firstCandidate.shapeKind,
          clusterRect: { ...firstCandidate.clusterRect, x: firstCandidate.clusterRect.x + dx, y: firstCandidate.clusterRect.y + dy },
          rects: firstCandidate.rects.map(r => ({ ...r, x: r.x + dx, y: r.y + dy })),
        };
        fallback = moved;
        if (!violatesHardRules(moved, numberObstacles, obstacles)) {
          break;
        }
      }
      best = fallback;
    }

    if (!best) {
      continue; // kan alleen bij lege vormenlijst — defensief
    }
    // Geauthoreerde eindverschuiving (zie HdGateBlockGatePreference.nudge).
    const nudge = preference?.nudge;
    const finalRects = nudge
      ? best.rects.map(r => ({ ...r, x: r.x + nudge.x, y: r.y + nudge.y }))
      : best.rects;
    emit(finalRects);
  }

  return placements;
}
